<script setup>
import { computed, onMounted, ref } from 'vue'
import { Badge, Button, PageHeader, Section, Spinner } from '@drama/ui'
import { ArrowLeft } from '@lucide/vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { getLlmConversationVideoMatch } from '../../api/llm/index.js'
import LongText from '../../components/LongText.vue'
import VideoMatchLogs from './video-match/VideoMatchLogs.vue'
import VideoMatchTrace from './video-match/VideoMatchTrace.vue'

const props = defineProps({
  runId: { type: String, required: true },
  conversationKey: { type: String, required: true },
  turnId: { type: String, required: true },
})

const { t, te } = useI18n()
const router = useRouter()
const loading = ref(false)
const error = ref('')
const detail = ref(null)

const title = computed(() => detail.value === null
  ? t('visualGameAdmin.llm.videoMatch.title')
  : t('visualGameAdmin.llm.videoMatch.titleWithTurn', { number: detail.value.context.turn_no }))

onMounted(load)

async function load() {
  loading.value = true
  error.value = ''
  detail.value = null
  try {
    detail.value = await getLlmConversationVideoMatch(props.runId, props.conversationKey, props.turnId)
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

function back() {
  router.push({
    name: 'llm-conversations',
    query: {
      run_id: props.runId,
      conversation_key: props.conversationKey,
      turn_id: props.turnId,
    },
  })
}

function roleLabel(role) {
  switch (role) {
    case 'kp':
    case 'npc':
    case 'npc_action_kp':
      return t(`visualGameAdmin.llm.conversations.roles.${role}`)
    default:
      return role
  }
}

function modeLabel(mode) {
  switch (mode) {
    case 'act':
    case 'say':
      return t(`visualGameAdmin.llm.conversations.detail.modes.${mode}`)
    default:
      return mode
  }
}

function resultStatus(context) {
  if (context.match_method !== null) return 'matched'
  return context.match_reason === null ? 'not_evaluated' : 'unmatched'
}

function resultVariant(context) {
  switch (resultStatus(context)) {
    case 'matched':
      return 'success'
    case 'unmatched':
      return 'warning'
    case 'not_evaluated':
      return 'neutral'
    default:
      return 'neutral'
  }
}

function methodLabel(method) {
  switch (method) {
    case 'exact_transition':
    case 'semantic_reuse':
    case 'intent_video':
      return t(`visualGameAdmin.llm.videoMatch.methods.${method}`)
    case null:
      return t('visualGameAdmin.llm.videoMatch.methods.none')
    default:
      return method
  }
}

function captureVariant(capture) {
  switch (capture) {
    case 'complete':
      return 'success'
    case 'reconstructed':
      return 'warning'
    default:
      return 'neutral'
  }
}

function captureLabel(capture) {
  const key = `visualGameAdmin.llm.videoMatch.capture.${capture}`
  return te(key) ? t(key) : capture
}

function captureDescription(capture) {
  const key = `visualGameAdmin.llm.videoMatch.captureDescription.${capture}`
  return te(key) ? t(key) : capture
}

function resultReason(context) {
  return context.match_reason === null
    ? t('visualGameAdmin.llm.videoMatch.result.noReason')
    : context.match_reason
}
</script>

<template>
  <div class="video-match-detail vga-page" data-testid="video-match-detail-page">
    <PageHeader :title="title">
      <template #actions>
        <Button variant="ghost" @click="back">
          <ArrowLeft :size="16" />
          {{ t('visualGameAdmin.llm.videoMatch.back') }}
        </Button>
      </template>
    </PageHeader>

    <div v-if="loading" class="video-match-detail__state">
      <Spinner size="lg" :label="t('visualGameAdmin.llm.videoMatch.loading')" />
    </div>
    <div v-else-if="error" class="video-match-detail__state">
      <p class="vga-error" role="alert">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>

    <template v-else-if="detail !== null">
      <Section>
        <div class="video-match-detail__overview">
          <div class="video-match-detail__context">
            <div class="video-match-detail__identity">
              <div>
                <span>{{ t('visualGameAdmin.llm.videoMatch.context.scenario') }}</span>
                <strong>{{ detail.context.scenario_title }}</strong>
              </div>
              <div>
                <span>{{ t('visualGameAdmin.llm.videoMatch.context.agent') }}</span>
                <strong>{{ roleLabel(detail.context.agent_role) }} · {{ modeLabel(detail.context.mode) }}</strong>
              </div>
            </div>
            <LongText
              :label="t('visualGameAdmin.llm.videoMatch.context.input')"
              :text="detail.context.input"
              :copy-text="detail.context.input"
              :collapse-over="500"
              markdown
            />
            <p class="video-match-detail__ids vga-mono">
              {{ detail.context.run_id }} · {{ detail.context.conversation_key }} · {{ detail.context.turn_id }}
            </p>
          </div>

          <aside class="video-match-detail__result">
            <div class="video-match-detail__result-head">
              <span>{{ t('visualGameAdmin.llm.videoMatch.result.title') }}</span>
              <Badge :variant="resultVariant(detail.context)">
                {{ t(`visualGameAdmin.llm.videoMatch.result.status.${resultStatus(detail.context)}`) }}
              </Badge>
            </div>
            <strong>{{ methodLabel(detail.context.match_method) }}</strong>
            <p>{{ resultReason(detail.context) }}</p>
            <dl>
              <div>
                <dt>{{ t('visualGameAdmin.llm.videoMatch.result.resource') }}</dt>
                <dd class="vga-mono">{{ detail.context.resource_id === null ? '—' : detail.context.resource_id }}</dd>
              </div>
              <div>
                <dt>{{ t('visualGameAdmin.llm.videoMatch.result.similarity') }}</dt>
                <dd>{{ detail.context.match_similarity === null ? '—' : detail.context.match_similarity.toFixed(4) }}</dd>
              </div>
            </dl>
          </aside>
        </div>
      </Section>

      <Section :title="t('visualGameAdmin.llm.videoMatch.traceTitle')">
        <div class="video-match-detail__capture">
          <Badge :variant="captureVariant(detail.trace.capture)" size="sm">
            {{ captureLabel(detail.trace.capture) }}
          </Badge>
          <p>{{ captureDescription(detail.trace.capture) }}</p>
        </div>
        <VideoMatchTrace :trace="detail.trace" :resources="detail.resources" />
      </Section>

      <Section :title="t('visualGameAdmin.llm.videoMatch.logsTitle')">
        <VideoMatchLogs :logs="detail.logs" />
      </Section>
    </template>
  </div>
</template>

<style scoped>
.video-match-detail {
  display: grid;
  gap: var(--drama-space-lg);
}

.video-match-detail__state {
  display: grid;
  min-height: 320px;
  place-items: center;
  align-content: center;
  gap: var(--drama-space-lg);
}

.video-match-detail__overview {
  display: grid;
  grid-template-columns: minmax(0, 1.65fr) minmax(280px, 0.85fr);
  gap: var(--drama-space-xl);
}

.video-match-detail__context {
  display: grid;
  align-content: start;
  gap: var(--drama-space-lg);
  min-width: 0;
}

.video-match-detail__identity {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--drama-space-lg);
}

.video-match-detail__identity div {
  display: grid;
  gap: var(--drama-space-xs);
}

.video-match-detail__identity span,
.video-match-detail__result-head > span,
.video-match-detail dt {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.video-match-detail__ids {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
  overflow-wrap: anywhere;
}

.video-match-detail__result {
  display: grid;
  align-content: start;
  gap: var(--drama-space-md);
  padding: var(--drama-space-lg);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-lg);
  background: var(--drama-color-surface-alt);
}

.video-match-detail__result-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.video-match-detail__result > strong {
  font-size: var(--drama-font-size-xl);
}

.video-match-detail__result > p,
.video-match-detail__capture p {
  margin: 0;
  color: var(--drama-color-text-muted);
  line-height: 1.6;
}

.video-match-detail__result dl {
  display: grid;
  gap: var(--drama-space-sm);
  margin: var(--drama-space-sm) 0 0;
  padding-top: var(--drama-space-md);
  border-top: 1px solid var(--drama-color-border);
}

.video-match-detail__result dl div {
  display: grid;
  gap: var(--drama-space-xs);
}

.video-match-detail__result dd {
  margin: 0;
  overflow-wrap: anywhere;
}

.video-match-detail__capture {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  width: min(100%, 980px);
  margin: 0 auto var(--drama-space-lg);
  padding-bottom: var(--drama-space-md);
  border-bottom: 1px solid var(--drama-color-border);
}

@media (max-width: 860px) {
  .video-match-detail__overview {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 680px) {
  .video-match-detail__identity {
    grid-template-columns: 1fr;
  }

  .video-match-detail__capture {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
